/**
 * The allowlist, as system state.
 *
 * Approving a sender in the Screener rewrites this list; it is never a file an
 * operator uploads. It lives in an addressable event encrypted under the NIP-44
 * conversation key between the account owner and the ingest identity, so both
 * the owner and postrd read it while the relay holds only ciphertext.
 *
 * The counterparty differs by side: the owner reads with the ingest pubkey,
 * postrd reads with the owner's. Deriving it from the event author works for
 * one side and fails with an opaque hmac error on the other.
 */
import type { Event as NostrEvent } from "nostr-tools";
import { gzipSync, gunzipSync } from "fflate";
import type { RelayConnection } from "./relay";
import type { MailSigner } from "./signer";

export const KIND_APP_DATA = 30078;
export const IDENTIFIER = "postr/contacts";

export interface ContactList {
  version: number;
  updated: string;
  addresses: string[];
}

/**
 * Read the current list.
 *
 * `counterparty` is the other half of the conversation key — the ingest
 * identity when the owner reads their own list.
 */
export async function fetchContacts(
  relay: RelayConnection,
  ownerPubkey: string,
  signer: MailSigner,
  counterparty: string,
): Promise<ContactList | null> {
  const events = await relay.list({
    kinds: [KIND_APP_DATA],
    authors: [ownerPubkey],
    "#d": [IDENTIFIER],
    limit: 1,
  });
  const newest = events.sort((a, b) => b.created_at - a.created_at)[0];
  if (!newest) return null;

  const plaintext = await signer.nip44Decrypt(counterparty, newest.content);
  const json = new TextDecoder().decode(gunzipSync(decodeBody(plaintext)));
  return JSON.parse(json) as ContactList;
}

/**
 * Publish a new version.
 *
 * Addresses are gzipped first: 1,752 of them are about 44KB raw, close to
 * NIP-44's 64KB ceiling, and compress to roughly a third of that.
 */
export async function publishContacts(
  relay: RelayConnection,
  signer: MailSigner,
  counterparty: string,
  addresses: string[],
  version: number,
  /** The newest list's timestamp; this one is stamped after it. */
  after = 0,
): Promise<NostrEvent> {
  const list: ContactList = {
    version,
    updated: new Date().toISOString(),
    addresses: [...new Set(addresses.map((a) => a.trim().toLowerCase()).filter(Boolean))].sort(),
  };
  const squeezed = gzipSync(new TextEncoder().encode(JSON.stringify(list)));
  const content = await signer.nip44Encrypt(counterparty, toBase64(squeezed));

  const event = await signer.signEvent({
    kind: KIND_APP_DATA,
    created_at: Math.max(Math.floor(Date.now() / 1000), after + 1),
    tags: [["d", IDENTIFIER]],
    content,
  });
  await relay.publish(event);
  return event;
}

/**
 * The NIP-44 plaintext must be text.
 *
 * A Go string tolerates arbitrary bytes, so writing raw gzip into it looks
 * correct from Go — but every other implementation decodes that plaintext as
 * UTF-8, and invalid sequences become U+FFFD. Base64 keeps the body printable
 * so both sides agree.
 */
export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Accepts base64, and raw gzip from a list published before the format was fixed. */
export function decodeBody(plaintext: string): Uint8Array {
  const looksGzip = plaintext.charCodeAt(0) === 0x1f && plaintext.charCodeAt(1) === 0x8b;
  if (!looksGzip) {
    try {
      const binary = atob(plaintext);
      return Uint8Array.from(binary, (c) => c.charCodeAt(0));
    } catch {
      /* fall through to the legacy shape */
    }
  }
  return Uint8Array.from(plaintext, (c) => c.charCodeAt(0) & 0xff);
}

/** An in-memory view for the hot path: is this sender known? */
export class Allowlist {
  private readonly set: Set<string>;

  constructor(addresses: string[] = []) {
    this.set = new Set(addresses.map((a) => a.trim().toLowerCase()).filter(Boolean));
  }

  /** Exact address only. Domain-wide trust would let one newsletter vouch for a provider. */
  has(address: string): boolean {
    return this.set.has(address.trim().toLowerCase());
  }

  add(address: string): void {
    const normalized = address.trim().toLowerCase();
    if (normalized) this.set.add(normalized);
  }

  get size(): number {
    return this.set.size;
  }

  toArray(): string[] {
    return [...this.set].sort();
  }
}
